// Drag-and-drop and keyboard zone assignment for inbox triage.
import { useCallback, useRef } from 'react';
import {
  applyInboxDragEnd,
  applyInboxZoneAssign,
} from '@/features/organize/utils/applyInboxDragEnd';

/**
 * @param {Object} params
 * @param {import('firebase/auth').User | null | undefined} params.user
 * @param {Function} params.updateTask
 * @param {{ key?: string } | null | undefined} params.activeGroup
 * @param {Map<string, string>} params.pendingProjectNames
 * @param {Map<string, string>} params.projectDisplayMap
 * @param {Map<string, string>} params.pendingContextNames
 * @param {Map<string, string>} params.contextDisplayMap
 * @param {(task: import('@/features/tasks/types').Task | null) => void} params.setActiveTask
 */
export function useInboxViewControllerDrag({
  user,
  updateTask,
  activeGroup,
  pendingProjectNames,
  projectDisplayMap,
  pendingContextNames,
  contextDisplayMap,
  setActiveTask,
}) {
  const handleDragStart = useCallback(
    ({ active }) => setActiveTask(active.data?.current?.task ?? null),
    [setActiveTask],
  );

  const handleDragEnd = useCallback(
    async ({ active, over }) => {
      setActiveTask(null);
      await applyInboxDragEnd({
        active,
        over,
        user,
        updateTask,
        activeGroupKey: activeGroup?.key,
        pendingProjectNames,
        projectDisplayMap,
        pendingContextNames,
        contextDisplayMap,
      });
    },
    [
      setActiveTask,
      user,
      updateTask,
      activeGroup?.key,
      pendingProjectNames,
      projectDisplayMap,
      pendingContextNames,
      contextDisplayMap,
    ],
  );

  const handleDragCancel = useCallback(() => setActiveTask(null), [setActiveTask]);

  const zoneAssignInFlightRef = useRef(false);

  const handleZoneKeyboardAssign = useCallback(
    async (zoneId, taskId) => {
      if (!taskId || zoneAssignInFlightRef.current) return;
      zoneAssignInFlightRef.current = true;
      try {
        await applyInboxZoneAssign({
          taskId,
          zoneId,
          user,
          updateTask,
          activeGroupKey: activeGroup?.key,
          pendingProjectNames,
          projectDisplayMap,
          pendingContextNames,
          contextDisplayMap,
        });
      } finally {
        zoneAssignInFlightRef.current = false;
      }
    },
    [
      user,
      updateTask,
      activeGroup?.key,
      pendingProjectNames,
      projectDisplayMap,
      pendingContextNames,
      contextDisplayMap,
    ],
  );

  return {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleZoneKeyboardAssign,
  };
}
